import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import CartLoader from "../../../../Helper/Loaders/CartLoader";
import { getDiagnosticsAnalyticsList } from "../../../../Api/BmsFaultsApi";
import { formatDateRange } from "../../../../Helper/Analytics/AnalyticsHelper";
import BreakdownChart from "../../../BmsFaults/Analytics/BreakdownChart";
import BMSFaultChart from "../../../BmsFaults/Components/StyledComponents/BMSFaultChart";
import SecondsFormatter, { SecondsFormatterDetailed } from "../../../../Helper/Formatters/SecondsFormatter";

const transformChartData = (responseData) => {
  const transformedChartData = responseData.map(item => {
    const chartItem = {
      alertName: item.xLabel,
      autoResolved: 0,
      manualResolved: 0,
    };
    item.metricsArray.forEach(metric => {
      if (metric.type === "AutoResolvedAverage") chartItem.autoResolved = metric.value;
      else if (metric.type === "ManualResolvedAverage") chartItem.manualResolved = metric.value;
    });
    return chartItem;
  });
  return transformedChartData.sort((a, b) => {
    const totalA = (a.autoResolved || 0) + (a.manualResolved || 0);
    const totalB = (b.autoResolved || 0) + (b.manualResolved || 0);
    return totalB - totalA;
  });
};

const buildChartConfig = (chartData, resolutionType) => {
  if (!chartData || chartData.length === 0) {
    return { series: [], options: {}, categories: [] };
  }

  let filteredData = chartData.filter(item =>
    resolutionType === "auto" ? item.autoResolved > 0 : item.manualResolved > 0
  );
  if (filteredData.length === 0) {
    return { series: [], options: {}, categories: [] };
  }

  filteredData = filteredData.sort((a, b) => {
    const valueA = resolutionType === "auto" ? a.autoResolved : a.manualResolved;
    const valueB = resolutionType === "auto" ? b.autoResolved : b.manualResolved;
    return valueB - valueA;
  });

  const categories = filteredData.map(item => item.alertName);
  const rawData = resolutionType === "auto"
    ? filteredData.map(item => item.autoResolved || 0)
    : filteredData.map(item => item.manualResolved || 0);

  const maxValue = Math.max(...rawData);
  const NO_VALUE_MIN_WIDTH = maxValue > 0 ? maxValue * 0.02 : 1;
  const MIN_WIDTH = maxValue > 0 ? maxValue * 0.03 : 1;

  const seriesData = rawData.map(value => {
    if (!value) return NO_VALUE_MIN_WIDTH;
    if (value >= MIN_WIDTH) return value;
    return MIN_WIDTH;
  });

  const formattedLabels = rawData.map(val => SecondsFormatterDetailed(val, 'short'));
  const longestLabelLength = Math.max(...formattedLabels.map(label => label.length));
  const bufferPercentage = Math.max(0.30, longestLabelLength * 0.04);
  const xAxisMax = Math.max(...seriesData) * (1 + bufferPercentage);

  const series = [
    {
      name: resolutionType === "auto" ? "Auto Resolved" : "Manually Resolved",
      data: seriesData,
    },
  ];

  const options = {
    chart: { stacked: false },
    plotOptions: {
      bar: {
        horizontal: true,
        dataLabels: { position: "top" },
      },
    },
    colors: ["#0B84FE"],
    dataLabels: {
      enabled: true,
      formatter: (value, opts) => {
        const dataPointIndex = opts?.dataPointIndex ?? 0;
        const rawValue = rawData[dataPointIndex] ?? value;
        return SecondsFormatterDetailed(rawValue, 'short');
      },
      style: { fontSize: "12px", fontWeight: 400, colors: ["#494949"] },
      offsetX: 10,
      textAnchor: "start",
    },
    states: { active: { filter: { type: "none" } } },
    xaxis: { categories, max: xAxisMax },
    legend: { show: false },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val, opts) => {
          const rawValue = rawData[opts?.dataPointIndex] ?? val;
          return SecondsFormatterDetailed(rawValue, 'short');
        },
      },
    },
  };

  return { series, options, categories };
};

const toggleButtonSx = (active) => ({
  fontSize: "12px",
  lineHeight: "16px",
  fontWeight: active ? 500 : 400,
  textTransform: "none",
  borderRadius: "20px",
  padding: "6px 14px",
  backgroundColor: active ? "#F7F7FF" : "transparent",
  color: active ? "#6D72F6" : "#494949",
  border: active ? "1px solid #6D72F6" : "1px solid #E6E6E6",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: active ? "#F7F7FF" : "#F8F8F8",
    border: active ? "1px solid #6D72F6" : "1px solid #E6E6E6",
    boxShadow: "none",
  },
});

const BatteryResolutionDurationChart = ({ batteryID, startDate, endDate, onBarClick }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolutionType, setResolutionType] = useState("auto");

  useEffect(() => {
    if (!batteryID || !startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);
    getDiagnosticsAnalyticsList({}, {
      startDate,
      endDate,
      select: ["AverageResolutionTime"],
      filters: [{ name: "batteryID", values: [batteryID] }],
      groupBy: ["errorDescription", "ResolutionType"],
      granularity: null,
    }).then(response => {
      if (cancelled) return;
      const responseData = response?.response?.data || [];
      setChartData(transformChartData(responseData));
    }).catch(() => {
      if (!cancelled) setChartData([]);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [batteryID, startDate, endDate]);

  const { series, options, categories } = buildChartConfig(chartData, resolutionType);
  const hasData = series.length > 0 && categories.length > 0;

  // Merge dataPointSelection event into chart options when onBarClick is provided
  const chartOptions = useMemo(() => {
    if (!onBarClick) return options;
    return {
      ...options,
      chart: {
        ...options.chart,
        events: {
          ...(options.chart?.events || {}),
          dataPointSelection: (event, chartContext, config) => {
            const { dataPointIndex } = config;
            if (dataPointIndex >= 0 && categories[dataPointIndex]) {
              // Pass resolutionType so the drilldown can pre-filter by resolution status
              onBarClick(categories[dataPointIndex], resolutionType);
            }
          },
        },
      },
    };
  }, [options, onBarClick, categories, resolutionType]);

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        border: "1px solid #E6E6E6",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 22px",
        }}
      >
        <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
          Battery System Fault Avg. Resolution Duration Breakdown
        </Typography>
        <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>
          {formatDateRange(startDate, endDate)}
        </Typography>
      </Box>

      <Box
        sx={{
          padding: "12px 28px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "120px" }}>
            <CartLoader />
          </Box>
        ) : (
          <>
            <Box>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Button
                  variant={resolutionType === "auto" ? "contained" : "outlined"}
                  onClick={() => setResolutionType("auto")}
                  sx={toggleButtonSx(resolutionType === "auto")}
                >
                  Auto Resolved
                </Button>
                <Button
                  variant={resolutionType === "manual" ? "contained" : "outlined"}
                  onClick={() => setResolutionType("manual")}
                  sx={toggleButtonSx(resolutionType === "manual")}
                >
                  Manually Resolved
                </Button>
              </Box>
              <Typography variant="fw600s12c8D8D8D" sx={{ textTransform: "uppercase", color: "#8F8F8F" }}>
                Fault Name
              </Typography>
            </Box>
            {!hasData ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80px" }}>
                <Typography variant="fw400s14c686868">
                  {chartData.length === 0
                    ? "No chart data available"
                    : `No data available for ${resolutionType === "auto" ? "Auto Resolved" : "Manually Resolved"} faults`}
                </Typography>
              </Box>
            ) : (
              <BMSFaultChart drillDownEnabled={!!onBarClick}>
                <BreakdownChart
                  categories={categories}
                  series={series}
                  options={chartOptions}
                  minVisibilityHeight={90}
                  xAxisTickFormatter={(value) => SecondsFormatter(value, 'short')}
                  labelWidth="260px"
                />
              </BMSFaultChart>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default BatteryResolutionDurationChart;
